import React, { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  isCheckoutAvailable,
  shouldShowMeasurementsWhenGoingBack,
  showHamburgerMenu,
  showMeasurements,
  showMeasurementsWhenGoingBack
} from 'ducks/layout';
import {
  hideSaveVpcSheet,
  isVpcSheetOpen,
  hasSaveVpcSheetBack,
  getSaveVpcSheetOpenedFromPrint,
  showPrintQuestionSheet,
  setSaveVpcSheetOpenedFromPrint
} from 'ducks/sheets';
import Body from 'ui/Typography/Body';
import { useIntl } from 'react-intl';
import SkapaTabs from 'ui/Tabs/SkapaTabs';
import { Tab, TabPanel } from '@ingka/tabs';
import { NotificationDataSourceEnum, ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { CopyDesignCodeStateEnum } from '@inter-ikea-kompis/component-copy-design-code';
import { NotificationType, SendDesign } from 'ui/SendDesign/SendDesign';
import { getVpcCode } from 'ducks/vpc';
import Title from 'ui/Typography/Title';
import SheetBox from 'ui/Sheet/SheetBox';
import SheetHeader from 'ui/Sheet/SheetHeader';
import withLoading from 'ui/withLoading/withLoading';
import { isMobile, usesVirtualKeyboard } from 'utils/platform';
import Sheet from 'ui/Sheet/Sheet';
import { CopyDesignCode } from 'components/CopyDesignCode';
import styled from 'styled-components';
import theme from 'ui/theme';
import { useAppDispatch } from 'ducks/hooks';
import { isHelpLinesActive, setHelpLinesLabelsVisible, setHelpLinesVisible } from 'ducks/helpLines';
import { useServiceSettings } from 'contexts/serviceSettings';

const VirtualKeyboardPadding = styled.div`
  margin-bottom: 340px;
`;
const NoPadding = styled.div``;

const LoadingSheetBox = withLoading(SheetBox);

const SaveVPCSheet = () => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const visible = useSelector(isVpcSheetOpen);
  const checkoutAvailable = useSelector(isCheckoutAvailable);
  const vpcCode = useSelector(getVpcCode);
  const withoutBackButton = useSelector(hasSaveVpcSheetBack);
  const showMeasurementLinesWhenNavigatingBackToPlanner = useSelector(
    shouldShowMeasurementsWhenGoingBack
  );
  const helpLinesSystemIsActive = useSelector(isHelpLinesActive);
  const { dexfSettings } = useServiceSettings();
  const notificationDataSource = dexfSettings.kompis.serviceSettings.notificationDataSource;
  const enableSendDesign = notificationDataSource === NotificationDataSourceEnum.dexf;
  const [notificationType, setNotificationType] = useState<NotificationType>(
    !isMobile() && !enableSendDesign ? 'qrcode' : 'email'
  );

  const [copyDesignCodeState, setCopyDesignCodeState] = useState<CopyDesignCodeStateEnum>(
    CopyDesignCodeStateEnum.default
  );

  const openedFromPrint = useSelector(getSaveVpcSheetOpenedFromPrint);

  const onClose = () => {
    if (openedFromPrint) {
      dispatch(setSaveVpcSheetOpenedFromPrint(false));
    }
    // Always re-enable help lines if system is active
    if (helpLinesSystemIsActive) {
      dispatch(setHelpLinesVisible(true));

      if (showMeasurementLinesWhenNavigatingBackToPlanner) {
        dispatch(setHelpLinesLabelsVisible(true));
      }
    }

    // if measurement lines was active before opening this sheet we activate them again
    if (showMeasurementLinesWhenNavigatingBackToPlanner) {
      dispatch(showMeasurements());
      dispatch(showMeasurementsWhenGoingBack(false));
    }
    dispatch(hideSaveVpcSheet());
    setCopyDesignCodeState(CopyDesignCodeStateEnum.default);
  };

  const goBackAndShowHamburgerMenu = () => {
    onClose();

    if (openedFromPrint) {
      dispatch(setSaveVpcSheetOpenedFromPrint(false));
      setTimeout(() => dispatch(showPrintQuestionSheet(true)));
    } else {
      setTimeout(() => dispatch(showHamburgerMenu()), 10);
    }
  };

  const tabs = useMemo(() => {
    const tabs = [
      {
        label: formatMessage({ id: 'storageone.email' }),
        type: 'email'
      }
    ];
    if (!isMobile()) {
      tabs.push({
        label: formatMessage({ id: 'storageone.qrCodeSaveDesignSheetTabTitle' }),
        type: 'qrcode'
      });
    }
    return enableSendDesign ? tabs : tabs.slice(2);
  }, [formatMessage, enableSendDesign]);

  const scrollIntoView = () => {
    virtualKeyboardPaddingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const virtualKeyboardPaddingRef = useRef<any>();
  const BottomPadding = usesVirtualKeyboard() ? VirtualKeyboardPadding : NoPadding;

  return (
    <Sheet onOverlayClick={onClose} visible={visible}>
      <SheetHeader
        backIcon={withoutBackButton ? true : false}
        onCloseButtonClick={onClose}
        onBackButtonClick={goBackAndShowHamburgerMenu}
      />
      <LoadingSheetBox isLoading={!vpcCode}>
        <Title pb="s" type={ThemeFontStyleTypeEnum.headingMedium}>
          {formatMessage({ id: 'storageone.saveDesign' })}
        </Title>
        {!checkoutAvailable && (
          <Body fontWeight="bold" type={ThemeFontStyleTypeEnum.bodyMedium}>
            {formatMessage({ id: 'storageone.saveDesignInvalidConfigurationWarning' })}
          </Body>
        )}
        <Body type={ThemeFontStyleTypeEnum.bodyMedium}>
          {formatMessage({ id: 'storageone.saveDesignInfo' })}
        </Body>
        <CopyDesignCode
          copyDesignCodeState={copyDesignCodeState}
          setCopyDesignCodeState={setCopyDesignCodeState}
        />
        <Body
          type={ThemeFontStyleTypeEnum.bodySmall}
          color={theme.colors.grey[700]}
          mt="-8px"
          mb="xl"
        >
          {formatMessage({ id: 'storageone.saveDesignIllustrationCaption' })}
        </Body>
        <div ref={virtualKeyboardPaddingRef} />
        {tabs.length > 0 && (
          <>
            <SkapaTabs
              defaultActiveTab={tabs[0].type}
              onTabChanged={(tab: React.SetStateAction<string | null>) =>
                tab && setNotificationType(tab as NotificationType)
              }
              tabs={tabs.map(({ label, type }) => (
                <Tab key={type} tabPanelId={type} data-testid={type} text={label} />
              ))}
              tabPanels={tabs.map(({ type }) => (
                <TabPanel key={type} tabPanelId={type} style={{ padding: 0 }}>
                  {visible && vpcCode && (
                    <SendDesign
                      notificationType={notificationType}
                      vpcCode={vpcCode}
                      onClick={scrollIntoView}
                    />
                  )}
                </TabPanel>
              ))}
            />
          </>
        )}

        <BottomPadding />
      </LoadingSheetBox>
    </Sheet>
  );
};

export default SaveVPCSheet;
